import { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  NumberInput,
  Switch,
  Button,
  Group,
  Stack,
  SimpleGrid,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import type { Coupon } from "../../types/domain";

export interface CouponFormValues {
  couponCode: string;
  descriptionEn: string;
  descriptionSw: string;
  frequency: number;
  discountPercentage: number;
  maxDiscountAmount: number;
  minApplicableAmount: number;
  endDate: Date | null;
  isActive: boolean;
  isDefault: boolean;
}

// Maps form values → the API body the backend CreateCouponDto expects.
export function toCouponBody(v: CouponFormValues): Partial<Coupon> & {
  frequency: number;
  description: { en: string; sw: string };
} {
  return {
    couponCode: v.couponCode.trim().toUpperCase(),
    frequency: v.frequency,
    description: { en: v.descriptionEn.trim(), sw: v.descriptionSw.trim() },
    discountPercentage: v.discountPercentage,
    maxDiscountAmount: v.maxDiscountAmount,
    minApplicableAmount: v.minApplicableAmount,
    endDate: new Date(v.endDate ?? new Date()).toISOString(),
    isActive: v.isActive,
    isDefault: v.isDefault,
  };
}

export function CouponFormModal({
  opened,
  onClose,
  editing,
  loading,
  onSubmit,
}: {
  opened: boolean;
  onClose: () => void;
  editing: Coupon | null;
  loading: boolean;
  onSubmit: (values: CouponFormValues) => void;
}) {
  const [v, setV] = useState<CouponFormValues>(blank());

  useEffect(() => {
    if (opened) {
      setV(
        editing
          ? {
              couponCode: editing.couponCode ?? "",
              descriptionEn: editing.description?.en ?? "",
              descriptionSw: editing.description?.sw ?? "",
              frequency: editing.frequency ?? 100,
              discountPercentage: editing.discountPercentage ?? 0,
              maxDiscountAmount: editing.maxDiscountAmount ?? 0,
              minApplicableAmount: editing.minApplicableAmount ?? 0,
              endDate: editing.endDate ? new Date(editing.endDate) : null,
              isActive: editing.isActive ?? true,
              isDefault: editing.isDefault ?? false,
            }
          : blank(),
      );
    }
  }, [opened, editing]);

  const valid =
    v.couponCode.trim().length >= 6 &&
    v.descriptionEn.trim() &&
    v.descriptionSw.trim() &&
    v.frequency >= 1 &&
    v.discountPercentage >= 0 &&
    v.discountPercentage <= 100 &&
    v.endDate;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editing ? "Edit promo code" : "New promo code"}
      centered
      size="lg"
    >
      <Stack>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput
            label="Code"
            placeholder="SAFIRI20"
            value={v.couponCode}
            onChange={(e) =>
              setV({ ...v, couponCode: e.currentTarget.value.toUpperCase() })
            }
            required
          />
          <NumberInput
            label="Usage limit (frequency)"
            min={1}
            max={1000}
            value={v.frequency}
            onChange={(n) => setV({ ...v, frequency: Number(n) || 0 })}
            required
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput
            label="Description (EN)"
            value={v.descriptionEn}
            onChange={(e) => setV({ ...v, descriptionEn: e.currentTarget.value })}
            required
          />
          <TextInput
            label="Description (SW)"
            value={v.descriptionSw}
            onChange={(e) => setV({ ...v, descriptionSw: e.currentTarget.value })}
            required
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <NumberInput
            label="Discount %"
            min={0}
            max={100}
            value={v.discountPercentage}
            onChange={(n) => setV({ ...v, discountPercentage: Number(n) || 0 })}
            required
          />
          <NumberInput
            label="Max discount (TZS)"
            min={0}
            thousandSeparator=","
            value={v.maxDiscountAmount}
            onChange={(n) => setV({ ...v, maxDiscountAmount: Number(n) || 0 })}
          />
          <NumberInput
            label="Min spend (TZS)"
            min={0}
            thousandSeparator=","
            value={v.minApplicableAmount}
            onChange={(n) => setV({ ...v, minApplicableAmount: Number(n) || 0 })}
          />
        </SimpleGrid>

        <DateInput
          label="Expires on"
          value={v.endDate}
          onChange={(d) => setV({ ...v, endDate: d as Date | null })}
          minDate={new Date()}
          required
        />

        <Group>
          <Switch
            label="Active"
            checked={v.isActive}
            onChange={(e) => setV({ ...v, isActive: e.currentTarget.checked })}
          />
          <Switch
            label="Default promo code"
            checked={v.isDefault}
            onChange={(e) => setV({ ...v, isDefault: e.currentTarget.checked })}
          />
        </Group>

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!valid}
            onClick={() => onSubmit(v)}
          >
            {editing ? "Save changes" : "Create promo code"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function blank(): CouponFormValues {
  return {
    couponCode: "",
    descriptionEn: "",
    descriptionSw: "",
    frequency: 100,
    discountPercentage: 10,
    maxDiscountAmount: 0,
    minApplicableAmount: 0,
    endDate: null,
    isActive: true,
    isDefault: false,
  };
}
